import { Inject, Injectable, Logger, Optional } from '@nestjs/common';
import {
  EnvKeyProvider,
  KEY_PROVIDER,
  KeyProvider,
  PrismaService,
  S3AdapterOptions,
  S3CompatibleAdapter,
  StorageAdapter,
  credentialContext,
  decryptToString,
  encryptWithKey,
  generateDataKey,
  maskKeyId,
} from '@cart/api-data-access';

export type DecryptPurpose = 'upload' | 'presign' | 'health_check' | 'migration' | 'purge' | 'backup' | 'restore';

export interface StorageConfigRow {
  id: string;
  organizationId: string;
  mode: string;
  bucket: string;
  region: string;
  endpoint: string | null;
  publicBaseUrl: string | null;
  pathPrefix: string | null;
}

export interface PlainCredentials {
  accessKeyId: string;
  secretAccessKey: string;
}

/**
 * The **only** code permitted to decrypt merchant storage credentials.
 *
 * Two design rules make this hold up in practice rather than on paper:
 *
 * 1. **It does not return secrets.** `adapterFor()` hands back a configured
 *    `StorageAdapter`; there is no public method returning a key string. A
 *    caller cannot log, serialize, or accidentally return in a DTO something
 *    it never receives. The one private path that materializes plaintext lives
 *    inside this file and is never exported.
 * 2. **Every decrypt is audited with its purpose.** Volume is predictable
 *    (roughly one per upload burst, one per health check), so an anomaly on
 *    decrypt rate is a real signal — which is what catches misuse that no
 *    permission check would, since the platform-admin role has no permission
 *    to read these at all.
 *
 * See docs/19-media-management-plan.md §10.
 */
@Injectable()
export class StorageCredentialVault {
  private readonly logger = new Logger(StorageCredentialVault.name);
  private readonly keys: KeyProvider;

  constructor(
    private readonly prisma: PrismaService,
    @Optional() @Inject(KEY_PROVIDER) keyProvider?: KeyProvider
  ) {
    this.keys = keyProvider ?? new EnvKeyProvider();
  }

  /**
   * Encrypts and stores a credential pair. Returns only the masked key ID —
   * the caller has the plaintext already (it just arrived in the request) and
   * must not receive it back from here.
   */
  async store(
    config: { id: string; organizationId: string },
    credentials: PlainCredentials,
    actorId: string
  ): Promise<{ accessKeyIdMasked: string }> {
    const dataKey = generateDataKey();
    const wrapped = await this.keys.wrap(
      dataKey,
      credentialContext(config.organizationId, config.id, 'dataKey')
    );

    const accessKeyIdEnc = encryptWithKey(
      dataKey,
      credentials.accessKeyId,
      credentialContext(config.organizationId, config.id, 'accessKeyId')
    );
    const secretKeyEnc = encryptWithKey(
      dataKey,
      credentials.secretAccessKey,
      credentialContext(config.organizationId, config.id, 'secretKey')
    );

    const existing = await this.prisma.storageCredential.findUnique({
      where: { storageConfigId: config.id },
      select: { id: true },
    });

    if (existing) {
      await this.prisma.storageCredential.update({
        where: { storageConfigId: config.id },
        data: {
          accessKeyIdEnc,
          secretKeyEnc,
          dataKeyWrapped: wrapped,
          rotatedAt: new Date(),
          rotatedBy: actorId,
        },
      });
    } else {
      await this.prisma.storageCredential.create({
        data: {
          storageConfigId: config.id,
          accessKeyIdEnc,
          secretKeyEnc,
          dataKeyWrapped: wrapped,
        },
      });
    }

    // Zero the data key rather than leaving it for the GC. Not a strong
    // guarantee in a managed runtime, but it shortens the window in which a
    // heap dump contains a usable key.
    dataKey.fill(0);

    return { accessKeyIdMasked: maskKeyId(credentials.accessKeyId) };
  }

  /**
   * Builds a ready-to-use adapter for a config. This is the entire public
   * surface for using a credential.
   */
  async adapterFor(config: StorageConfigRow, purpose: DecryptPurpose): Promise<StorageAdapter> {
    const credentials = await this.#decrypt(config, purpose);
    const options: S3AdapterOptions = {
      bucket: config.bucket,
      region: config.region || 'auto',
      endpoint: config.endpoint ?? undefined,
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      publicBaseUrl: config.publicBaseUrl ?? undefined,
      pathPrefix: config.pathPrefix ?? undefined,
      // R2 addresses buckets path-style behind an account endpoint; real S3
      // uses virtual-hosted style and no endpoint override.
      forcePathStyle: config.mode !== 'byos_s3',
    };
    return new S3CompatibleAdapter(options);
  }

  /**
   * Builds an adapter from credentials that have not been stored yet — the
   * setup wizard validates before persisting, so a merchant never ends up
   * with a saved config that has never worked.
   */
  adapterForUnsaved(config: Omit<StorageConfigRow, 'id' | 'organizationId'>, credentials: PlainCredentials): StorageAdapter {
    return new S3CompatibleAdapter({
      bucket: config.bucket,
      region: config.region || 'auto',
      endpoint: config.endpoint ?? undefined,
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      publicBaseUrl: config.publicBaseUrl ?? undefined,
      pathPrefix: config.pathPrefix ?? undefined,
      forcePathStyle: config.mode !== 'byos_s3',
    });
  }

  async hasCredentials(storageConfigId: string): Promise<boolean> {
    const row = await this.prisma.storageCredential.findUnique({
      where: { storageConfigId },
      select: { id: true },
    });
    return row !== null;
  }

  async deleteCredentials(storageConfigId: string): Promise<void> {
    await this.prisma.storageCredential.deleteMany({ where: { storageConfigId } });
  }

  /**
   * The only place plaintext credentials exist, and an **ECMAScript private
   * method** rather than a TypeScript `private` one.
   *
   * That distinction is the difference between a comment and a control:
   * TypeScript's `private` is erased at compile time, so the method would
   * still sit on the prototype and any JavaScript — a stray controller, a
   * debug endpoint, a future contributor reaching past the types — could call
   * it and get both credentials back. `#` is enforced by the runtime: outside
   * this class the method does not exist.
   *
   * If you are adding a method that returns what this returns, stop: that is
   * the leak this class is shaped to prevent.
   */
  async #decrypt(config: StorageConfigRow, purpose: DecryptPurpose): Promise<PlainCredentials> {
    const row = await this.prisma.storageCredential.findUnique({
      where: { storageConfigId: config.id },
      select: { accessKeyIdEnc: true, secretKeyEnc: true, dataKeyWrapped: true },
    });
    if (!row) {
      throw new Error(`No stored credentials for storage config ${config.id}`);
    }

    const dataKey = await this.keys.unwrap(
      row.dataKeyWrapped,
      credentialContext(config.organizationId, config.id, 'dataKey')
    );

    try {
      const accessKeyId = decryptToString(
        dataKey,
        row.accessKeyIdEnc,
        credentialContext(config.organizationId, config.id, 'accessKeyId')
      );
      const secretAccessKey = decryptToString(
        dataKey,
        row.secretKeyEnc,
        credentialContext(config.organizationId, config.id, 'secretKey')
      );

      // Audit line carries the purpose and never the material. Rate anomalies
      // on this line are the detection mechanism described in the plan.
      this.logger.log(
        `storage.credential.decrypt config=${config.id} org=${config.organizationId} purpose=${purpose}`
      );

      return { accessKeyId, secretAccessKey };
    } finally {
      dataKey.fill(0);
    }
  }
}
